// Pure helpers for rendering tool calls in the chat transcript the way a
// terminal coding agent (Claude Code / Codex) does: a one-line label plus a
// one-line result summary, with the heavy detail tucked behind an expander.
// No React here — keep it unit-testable.

export interface CodemodeCallDescription {
  /** distinct server keys touched via helpers.<server>.*, first-seen order */
  servers: string[];
  /** distinct tool names invoked via getData(), first-seen order */
  tools: string[];
  /** what the program mostly does */
  kind: "query" | "discover" | "run";
  /** compact one-line label for the card header, e.g. "opentargets_execute" */
  label: string;
}

// helpers.<server>.getData('<tool>', ...) — the only sanctioned data path.
const GET_DATA_RE = /helpers\.([a-zA-Z0-9_]+)\.getData\(\s*['"]([^'"]+)['"]/g;
// Discovery calls carry no data but tell us the program is introspecting.
const DISCOVER_RE = /helpers\.[a-zA-Z0-9_]+\.(?:listTools|describeTool)\b/;

/**
 * Parse a codemode_sandbox program to recover what it actually did, so the
 * card header can read like `Sandbox · opentargets_execute` instead of an
 * opaque `codemode sandbox`. Tolerant of partial code mid-stream.
 */
export function describeCodemodeCall(
  code: string | undefined,
): CodemodeCallDescription {
  const servers: string[] = [];
  const tools: string[] = [];
  if (typeof code === "string" && code) {
    GET_DATA_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = GET_DATA_RE.exec(code))) {
      const server = m[1];
      const toolName = m[2];
      if (server && !servers.includes(server)) servers.push(server);
      if (toolName && !tools.includes(toolName)) tools.push(toolName);
    }
  }

  let kind: CodemodeCallDescription["kind"] = "run";
  if (tools.length) kind = "query";
  else if (typeof code === "string" && DISCOVER_RE.test(code)) kind = "discover";

  let label: string;
  if (tools.length === 1) label = tools[0];
  else if (tools.length > 1) label = `${tools[0]} +${tools.length - 1}`;
  else if (kind === "discover") label = "discover tools";
  else label = "run";

  return { servers, tools, kind, label };
}

export type SummaryTone = "ok" | "error" | "warn";

export interface OutputSummary {
  /** short result descriptor, e.g. "3 of 13,182 results", "staged · 1,407 rows" */
  text: string;
  tone: SummaryTone;
}

// Keys whose ARRAY value is the result set worth counting (checked at any
// depth so GraphQL-style { disease: { associatedTargets: { rows: [...] } } }
// summarizes meaningfully instead of as "1 field"/"0 items").
const COUNT_ARRAY_KEYS = new Set([
  "results", "rows", "hits", "items", "edges", "records", "data", "entries",
  "docs", "targets", "drugs", "variants", "genes", "papers", "studies",
  "associations", "annotations",
]);
// Keys whose NUMBER value is a total/result count (the magnitude of the query).
// `row_count` is the spelling the WorkspaceDO staging path uses (see
// cf/server/lib/workspace.ts) — without it a paged workspace_query read reports
// only the page size and silently drops the "of N" half.
const TOTAL_KEYS = new Set([
  "count", "total", "totalcount", "total_rows", "totalrows", "numfound",
  "hitcount", "recordcount", "resultcount", "row_count", "rowcount",
]);

// Keys that are metadata/side-channels, never the "result" the user cares about.
function isMetaKey(k: string): boolean {
  return k.startsWith("_") || k === "citations";
}

/**
 * Best-effort one-line summary of a tool result for the collapsed card header.
 * Truth lives in the expanded view; this is just a scannable hint.
 */
export function summarizeToolOutput(output: unknown): OutputSummary {
  const prim = primitiveSummary(output);
  if (prim) return prim;

  const obj = output as Record<string, unknown>;

  const errored = errorSummary(obj);
  if (errored) return errored;

  const truncated = obj._truncated === true;
  if (truncated) return { text: "partial result rejected", tone: "error" };

  // Staged datasets: surface the row count — the data is queryable via SQL,
  // which is far more useful than "1 field".
  const staged = stagedRows(obj);
  if (staged) {
    return {
      text: staged.rows !== null ? `staged · ${grp(staged.rows)} rows` : "staged",
      tone: "ok",
    };
  }

  // Bounded deep search for the meaningful result count (nested/GraphQL shapes).
  const c = deepCount(obj);
  if (c) return formatCount(c.shown, c.total, truncated);

  const generic = genericArraySummary(obj, truncated);
  if (generic) return generic;

  const keys = Object.keys(obj).filter((k) => !isMetaKey(k));
  if (keys.length === 0) return { text: "done", tone: "ok" };
  return { text: countLabel(keys.length, "field"), tone: "ok" };
}

/** Summaries for non-plain-object outputs; null means "it's an object — continue". */
function primitiveSummary(output: unknown): OutputSummary | null {
  if (output === undefined || output === null) return { text: "", tone: "ok" };
  if (typeof output === "string") {
    const s = output.trim();
    if (!s) return { text: "", tone: "ok" };
    return { text: s.length > 48 ? `${s.length} chars` : s, tone: "ok" };
  }
  if (typeof output === "number" || typeof output === "boolean") {
    return { text: String(output), tone: "ok" };
  }
  if (Array.isArray(output)) {
    return output.length === 0
      ? { text: "no results", tone: "ok" }
      : { text: countLabel(output.length, "item"), tone: "ok" };
  }
  if (typeof output !== "object") return { text: "", tone: "ok" };
  return null;
}

/** Red-toned summary for error shapes; null if not an error. */
function errorSummary(obj: Record<string, unknown>): OutputSummary | null {
  const err = obj.error;
  if (err !== undefined && err !== null) {
    const msg =
      typeof err === "string"
        ? err
        : typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "failed";
		return { text: `error: ${msg}`, tone: "error" };
  }
  if (obj.success === false) return { text: "failed", tone: "error" };
  return null;
}

/** Count the first non-empty, non-metadata array property; null if none. */
function genericArraySummary(
  obj: Record<string, unknown>,
  truncated: boolean,
): OutputSummary | null {
  for (const [k, v] of Object.entries(obj)) {
    if (!isMetaKey(k) && Array.isArray(v) && v.length > 0) {
      const base = countLabel(v.length, "item");
      return truncated
        ? { text: `${base} · truncated`, tone: "warn" }
        : { text: base, tone: "ok" };
    }
  }
  return null;
}

/**
 * Detect a staged-dataset envelope and its row count, if known.
 *
 * THREE spellings are live at once and all three must be honoured, because
 * missing one is invisible: a staged envelope that isn't recognised falls
 * through to genericArraySummary, which counts its `tables` array — so a
 * 1,407-row dataset rendered as "2 items", the most misleading summary
 * possible for a result the user is about to SQL against.
 *   - `__staged` (double) — the Code Mode isolate's own envelope, `total_rows`.
 *   - `_staged`  (single) — cf's two staging paths: codemode-staging.ts (which
 *     carries `row_count`) and lossless-result.ts (which carries NO count at
 *     all, so "staged" with no number is the honest render there).
 *   - `_staging` — the MCP server-side StagingMetadata side-channel.
 */
function stagedRows(obj: Record<string, unknown>): { rows: number | null } | null {
  if (obj.__staged === true || obj._staged === true) {
    return { rows: firstFiniteNumber(obj.total_rows, obj.row_count) };
  }
  const meta = obj._staging;
  if (isRecord(meta)) {
    return { rows: firstFiniteNumber(meta.total_rows, meta.row_count) };
  }
  return null;
}

/** First argument that is a usable number; null if none is. */
function firstFiniteNumber(...values: unknown[]): number | null {
  for (const v of values) {
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

/** Narrow to a plain object (not null, not an array) without an assertion. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Wall clock for one tool call, formatted for the card header — or null when
 * there is nothing to show.
 *
 * The backend already measures this: `stampTiming` in
 * cf/server/lib/lossless-result.ts writes `_meta.timing.duration_ms` onto every
 * object-shaped tool result precisely so a reader can say WHICH source was
 * slow, not just that the turn was. Until this function existed the number was
 * computed, persisted to the trace, and then rendered nowhere.
 *
 * Non-object results (a bare string, an array) carry no `_meta` by
 * construction — stampTiming returns them untouched — so null is the correct
 * answer there rather than a fabricated 0.
 */
export function summarizeToolDuration(output: unknown): string | null {
  const ms = durationMsOf(output);
  if (ms === null) return null;
  if (ms < 1_000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(1)}s`;
  const seconds = Math.floor(ms / 1_000);
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

/** Read `_meta.timing.duration_ms`, rejecting anything that isn't a real span. */
function durationMsOf(output: unknown): number | null {
  if (!isRecord(output)) return null;
  const meta = output._meta;
  if (!isRecord(meta)) return null;
  const timing = meta.timing;
  if (!isRecord(timing)) return null;
  const ms = timing.duration_ms;
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) return null;
  return ms;
}

/**
 * Walk an object (bounded) for the largest result-array length under a known
 * collection key, and the largest total-count number. Returns null if neither
 * is present so callers can fall back to a shape description.
 */
function deepCount(root: unknown): { shown: number; total?: number } | null {
  let bestArray: number | null = null;
  let bestTotal: number | null = null;
  let nodes = 0;
  const walk = (node: unknown, depth: number): void => {
    if (node === null || typeof node !== "object" || depth > 4 || nodes > 300) {
      return;
    }
    nodes++;
    if (Array.isArray(node)) {
      for (const v of node) walk(v, depth + 1);
      return;
    }
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      const key = k.toLowerCase();
      if (Array.isArray(v) && COUNT_ARRAY_KEYS.has(key)) {
        if (bestArray === null || v.length > bestArray) bestArray = v.length;
      } else if (typeof v === "number" && TOTAL_KEYS.has(key)) {
        if (bestTotal === null || v > bestTotal) bestTotal = v;
      }
      walk(v, depth + 1);
    }
  };
  walk(root, 0);
  if (bestArray === null && bestTotal === null) return null;
  const shown = bestArray ?? bestTotal ?? 0;
  const total = bestTotal !== null && bestTotal !== shown ? bestTotal : undefined;
  return { shown, total };
}

function formatCount(
  shown: number,
  total: number | undefined,
  truncated: boolean,
): OutputSummary {
  const headline = shown > 0 ? shown : total ?? 0;
  let base: string;
  if (headline === 0) base = "no results";
  else if (total && total > shown && shown > 0)
    base = `${grp(shown)} of ${grp(total)} results`;
  else base = `${grp(headline)} result${headline === 1 ? "" : "s"}`;
  return truncated
    ? { text: `${base} · truncated`, tone: "warn" }
    : { text: base, tone: "ok" };
}

/** Group digits with thousands separators, deterministically (no locale). */
function grp(n: number): string {
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function countLabel(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}
